/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Sagas for Basket component
 *
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import get from 'lodash/get';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { requestSaga } from '@/main/sagas/requestSaga';
import {
    getBasketCountSuccessful,
    setBasketValid,
    setTableBasketParamsAction,
} from '@components/Table/actions/tableActions';
import { TABLE_LOAD_SIZE } from '@components/Table/constants';
import type { ITableBasketParams } from '@components/Table/reducers/tableReducer';
import {
    makeSelectorBasketContext,
    makeSelectorIsFastBasketPage,
    makeSelectorTableBasketCount,
    makeSelectorTableCurrentName,
} from '@components/Table/selectors/tableSelectors';
import { MapTableNameToBasketApiPath } from '@components/Table/types/tableMapTypes';
import { TableNameType } from '@components/Table/types/tableTypes';
import { pushGTMAnalytic } from '@modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import {
    EGTMBasketSectionType,
    MapTableNameToGTMBasketSectionType,
} from '@modules/analytics/GoogleTagManager/GTMOperations/GTMBasketOperations';
import { API_HOSTNAME, BASKET_LIST } from '@modules/app/Api';

import { basketGetInitList, basketGetListFailed, basketGetListSuccessful } from './basketActions';
import { BASKET_GET_LIST_START } from './basketConstants';
import { makeSelectorBasketList, makeSelectorInitBasketList } from './basketSelectors';
import type { IBasketGetListStartPayload, IBasketItem } from './basketTypes';

export function* basketGetListStartSaga(action: { payload: IBasketGetListStartPayload; type: string }): SagaIterator {
    const isFastBasketPage = yield select(makeSelectorIsFastBasketPage());

    // если используется "быстрая корзина" (корзина со стороны фронта)
    // то запрос корзины с бэкенда не требуется
    if (isFastBasketPage) {
        return;
    }

    try {
        const start: boolean = get(action, 'payload.start', false);
        const context = yield select(makeSelectorBasketContext());
        const basketList = yield select(makeSelectorBasketList());
        const initBasketList = yield select(makeSelectorInitBasketList());
        const queryString = [
            `from=${start ? 0 : basketList.length}`,
            `size=${TABLE_LOAD_SIZE}`,
            context ? `context=${context}` : '',
        ].filter(Boolean).join('&');
        const tableName: TableNameType = yield select(makeSelectorTableCurrentName());
        const url = API_HOSTNAME + MapTableNameToBasketApiPath[tableName] + BASKET_LIST + '?' + queryString;

        const basketData = yield call(requestSaga, url, 'GET');

        const list = get(basketData, 'elements', []).map((item: IBasketItem) => ({
            ...item,
            checked: true,
        }));
        const initList = get(basketData, 'elements', []).map((item: IBasketItem) => ({
            ...item,
            checked: true,
        }));
        const count = get(basketData, 'count', 0);
        const valid = get(basketData, 'valid', true);
        const params: ITableBasketParams = get(basketData, 'params');

        if (params) {
            yield put(setTableBasketParamsAction(params));
        }

        yield call(basketGtmShowSaga, count);
        yield put(basketGetListSuccessful({ list: start ? list : [].concat(basketList, list) }));
        yield put(basketGetInitList({ list: start ? initList : [].concat(initBasketList, initList) }));
        yield put(getBasketCountSuccessful(count));
        yield put(setBasketValid(valid));
    } catch (e) {
        yield put(basketGetListFailed());
    }
}

export function* basketGtmShowSaga(currentCount: number) {
    const prevCount: number | null = yield select(makeSelectorTableBasketCount());
    const tableName: TableNameType = yield select(makeSelectorTableCurrentName());
    const gtmSectionType: EGTMBasketSectionType | undefined = MapTableNameToGTMBasketSectionType[tableName];

    if (gtmSectionType && prevCount === 0 && currentCount !== prevCount) {
        yield put(
            pushGTMAnalytic({
                operation: GTMOperations.basket.onBasketButtonShow(gtmSectionType),
                withUserRole: true,
                withSusrId: true,
                withAccountType: true,
            }),
        );
    }
}

export function* basketSagas(): SagaIterator {
    yield all([takeEvery(BASKET_GET_LIST_START, basketGetListStartSaga)]);
}
